import {DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  catchError,
  first,
  forkJoin,
  from,
  map,
  mergeMap,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
  timeout,
  toArray
} from 'rxjs';
import {PAYMENT_METHOD} from 'src/app/shared/enums/payment-method.enum';
import {AccountStorePort} from '../../entities/account/domain/ports/account-store.port';
import {ACCOUNT_STORE} from '../../entities/account/domain/tokens/account-store.token';
import {ConsumptionService} from '../../entities/consumption/consumption.service';
import {ENTRY_TYPE} from '../../entities/entry/domain/enum/entry-type.enum';
import {Entry} from '../../entities/entry/domain/model/entry.model';
import {
  ProcessManualPaymentUseCase
} from '../../entities/payment/application/use-cases/process-manual-payment.use-case';
import {IApiPaymentTransactionResponse} from '../../entities/payment/domain/interfaces/payment-api.interface';
import {TicketEventService} from '../../entities/ticket-events/ticket-event.service';
import {PAYMENT_FLOW} from '../../enums/payment-flow.enum';
import {Logger} from '../../interfaces/Logger';
import {IPaymentDetails, IPaymentSummary,} from '../../interfaces/payment.interface';
import {NetworkConnectionService} from '../network-connection/network-connection.service';
import {TerminalManagerService} from '../terminal-manager/terminal-manager.service';
import {TRACKER_TYPE} from '../tracking/enums/tracker.enum';
import {TrackingService} from '../tracking/tracking.service';
import {createPaymentTracker} from '../tracking/utils/payment-tracker.util';
import {IntegratedPaymentService} from './payment-flows/integrated-payment.service';
import {NoConnectionPaymentService} from './payment-flows/no-connection-payment.service';

/**
 * Service responsible for handling payment operations throughout the application.
 * Manages different payment flows (manual, integrated, offline) and post-payment processes
 * such as ticket event handling and consumption printing.
 */
@Injectable({
  providedIn: 'root',
})
export class PaymentsService {
  private readonly ticketEventService = inject(TicketEventService);
  private readonly integratedPaymentService = inject(IntegratedPaymentService);
  private readonly processManualPaymentUseCase = inject(ProcessManualPaymentUseCase);
  private readonly noConnectionPaymentService = inject(
    NoConnectionPaymentService
  );
  private readonly logger = inject(Logger);
  private readonly terminalService = inject(TerminalManagerService);
  private readonly trackingService = inject(TrackingService);
  private readonly networkConnectionService = inject(NetworkConnectionService);
  private readonly consumptionService = inject(ConsumptionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly accountStoreService = inject<AccountStorePort>(ACCOUNT_STORE);

  private readonly trackPayment = createPaymentTracker(this.trackingService);

  private readonly _onCashPaymentDelivered: Subject<IPaymentSummary> =
    new Subject<IPaymentSummary>();
  onCashPaymentDelivered$ = this._onCashPaymentDelivered.asObservable();

  private readonly _onProcessingPayment: Subject<boolean> =
    new Subject<boolean>();
  onProcessingPayment$ = this._onProcessingPayment.asObservable();

  /**
   * Notifies subscribers when a cash payment has been delivered.
   *
   * @param paymentResume - Summary of the delivered payment
   */
  notifyOnCashPaymentDelivered(paymentResume: IPaymentSummary): void {
    this._onCashPaymentDelivered.next(paymentResume);
  }

  /**
   * Updates the payment processing state and notifies subscribers.
   *
   * @param processing - Boolean indicating if a payment is being processed
   */
  notifyOnProcessingPayment(processing: boolean): void {
    this._onProcessingPayment.next(processing);
  }

  /**
   * Processes a payment for an account using the appropriate payment flow.
   * Determines whether to use manual, integrated, or no-connection payment flow
   * based on payment method, terminal availability, and network connection.
   *
   * @param paymentDetails - Details of the payment to be processed
   * @param forceManualPayment - Optional flag to force usage of manual payment flow
   * @returns Observable with the payment transaction response
   */
  payAccountAmount(
    paymentDetails: IPaymentDetails,
    forceManualPayment?: boolean
  ): Observable<IApiPaymentTransactionResponse> {
    const isCardPayment =
      paymentDetails.paymentSummary.paymentMethod === PAYMENT_METHOD.card;

    const useManualFlow = forceManualPayment || !isCardPayment || (!this.terminalService.isTap2PayEnabled && !this.terminalService.currentTerminal);

    this.logger.debug(
      `Use manual flow: ${useManualFlow}.
      isCardPayment: ${isCardPayment},
      isTap2PayEnabled: ${this.terminalService.isTap2PayEnabled},
      hasTerminal: ${!!this.terminalService.currentTerminal}`
    );

    // Store the current trackId before the payment process starts
    const currentTrackId = this.getCurrentTrackId();

    // Track payment initiation
    this.trackPayment('Payment process initiated', {
      paymentMethod: paymentDetails.paymentSummary.paymentMethod,
      paymentType: paymentDetails.paymentSummary.paymentType,
      amount: paymentDetails.paymentSummary.totalAmount,
      entriesCount: paymentDetails.entriesToPay.length,
      isAccountClosed: paymentDetails.isAccountClosed,
      isTicketGenerated: paymentDetails.isTicketGenerated,
      flow: useManualFlow ? PAYMENT_FLOW.manual : 'integrated',
    });

    let paymentFlow$: Observable<IApiPaymentTransactionResponse>;

    if (useManualFlow) {
      this.logger.debug(
        `[PAYMENTS 💶💶] [MANUAL] [Init manual payment ${paymentDetails.paymentSummary.paymentMethod}]`
      );

      this.trackPayment('Using manual payment flow', {
        paymentMethod: paymentDetails.paymentSummary.paymentMethod,
        flow: PAYMENT_FLOW.manual,
      });

      paymentFlow$ = this.processManualPaymentUseCase.execute(paymentDetails).pipe(
        map(result => ({
          success: result.success,
          payment: result.payment,
          ticket: result.ticket,
          key: PAYMENT_FLOW.manual,
          code: result.success ? 200 : 500,
          message: null
        }))
      );
    } else {
      this.logger.debug(
        `[PAYMENTS 💶💶] [INTEGRATED] [Init integrated payment]`
      );

      const hasInternetConnection = this.networkConnectionService.isOnline;

      if (!hasInternetConnection) {
        this.trackPayment('Using no-connection payment flow', {
          flow: 'no-connection',
        });

        paymentFlow$ =
          this.noConnectionPaymentService.processPayment(paymentDetails);
      } else {
        this.trackPayment('Using integrated payment flow', {
          flow: 'integrated',
        });

        paymentFlow$ = this.integratedPaymentService.processPayment(paymentDetails);
      }
    }

    return paymentFlow$.pipe(
      first(),
      tap((payment) => {
        if (payment.success) {
          this.trackPayment('Payment processed successfully', {
            paymentId: payment.payment?._id,
            amount: paymentDetails.paymentSummary.totalAmount,
          });
        } else {
          this.trackPayment('Payment processing failed', {
            error: payment.message || 'Unknown error',
            amount: paymentDetails.paymentSummary.totalAmount,
          });
        }
      }),
      switchMap((payment: IApiPaymentTransactionResponse) => {
        return forkJoin({
          paymentResponse: of(payment),
          ticketEvent: this.ticketEventService.getCurrentTicketEvent(),
        });
      }),
      switchMap(({paymentResponse, ticketEvent}) => {
        if (ticketEvent && paymentResponse.success) {
          this.trackPayment('Removing ticket event after successful payment', {
            paymentId: paymentResponse.payment?._id,
            customData: {
              ticketEventId: ticketEvent._id
            }
          });

          return this.ticketEventService
            .deleteCurrentTicketEvent()
            .pipe(map(() => paymentResponse));
        }

        return of(paymentResponse);
      }),
      switchMap((paymentResponse) => {
        return this.handlePostPayment(paymentDetails, paymentResponse);
      }),
      tap((paymentResponse) => {
        this.trackPayment('Payment flow completed', {
          paymentId: paymentResponse.payment?._id,
          success: paymentResponse.success,
          flow: paymentResponse.key,
        });

        this.finalizePaymentTracking(currentTrackId);
      })
    );
  }

  /**
   * Gets the current trackId from the account store
   * @returns Current trackId or empty string if not available
   */
  private getCurrentTrackId(): string {
    const currentAccount = this.accountStoreService.getSelectedAccount();

    return currentAccount?.trackId ?? '';
  }

  /**
   * Finalizes payment tracking with a specific trackId
   * @param trackId - The trackId to use for finalization
   */
  private finalizePaymentTracking(trackId: string): void {
    this.trackingService.finalizeTracking(TRACKER_TYPE.payment, trackId);
  }

  /**
   * Handles post-payment operations based on the payment response.
   * Currently handles consumption entries printing for successful payments.
   *
   * @param paymentResponse - The payment transaction response
   * @returns Observable with the original payment response
   */
  private handlePostPayment(
    paymentDetails: IPaymentDetails,
    paymentResponse: IApiPaymentTransactionResponse
  ): Observable<IApiPaymentTransactionResponse> {
    if (paymentResponse.success) {
      this.trackPayment('Starting post-payment operations', {
        paymentId: paymentResponse.payment?._id,
      });

      return this.printConsumptionEntries(paymentDetails, paymentResponse).pipe(
        tap(() => {
          this.trackPayment('Finished post-payment operations', {
            paymentId: paymentResponse.payment?._id,
          });
        })
      );
    }

    return of(paymentResponse);
  }

  /**
   * Prints consumption entries associated with a payment.
   * Processes each consumption entry sequentially.
   *
   * @param paymentResponse - The payment transaction response containing entries
   * @returns Observable with the original payment response
   */
  private printConsumptionEntries(
    paymentDetails: IPaymentDetails,
    paymentResponse: IApiPaymentTransactionResponse
  ): Observable<IApiPaymentTransactionResponse> {
    const consumptionEntries = paymentDetails.entriesToPay.filter(
      (entry) => entry.type === ENTRY_TYPE.consumption
    );

    if (!consumptionEntries?.length) {
      return of(paymentResponse);
    }

    this.trackPayment('Starting consumption entries printing', {
      paymentId: paymentResponse.payment?._id,
      entriesCount: consumptionEntries.length,
    });

    return from(consumptionEntries).pipe(
      mergeMap((entry: Entry) => {
        return this.consumptionService
          .printConsumption(entry.associated_id, entry.quantity)
          .pipe(
            first(),
            takeUntilDestroyed(this.destroyRef),
            timeout({
              each: 2000,
              with: (error) => this.handlePrintingTimeout(entry, error),
            }),
            catchError((error) => this.handlePrintingError(entry, error))
          );
      }, 3),
      catchError((error) => this.handleMainPrintFlowError(error)),
      toArray(),
      map(() => paymentResponse),
      tap(() => {
        this.trackPayment('Consumption entries printing completed', {
          paymentId: paymentResponse.payment?._id,
        });
      })
    );
  }

  /**
   * Handles timeout errors when printing consumption entries.
   *
   * @param entry - The entry that was being processed
   * @param error - The error object from the timeout
   * @returns Observable with null to continue processing other entries
   */
  private handlePrintingTimeout(entry: Entry, error: unknown): Observable<null> {
    this.logger.error(
      new Error(
        `[PaymentsService] [printConsumptionEntries] [Timeout] Error printing consumption for entry ${entry.associated_id}`
      ),
      error
    );

    this.trackPayment('Timeout while printing consumption entry, continuing with next one', {
      error: `Timeout for entry ${entry.associated_id}`,
      customData: {entryId: entry.associated_id},
    });

    return of(null);
  }

  /**
   * Handles general errors when printing consumption entries.
   *
   * @param entry - The entry that was being processed
   * @param error - The error object from the printing operation
   * @returns Observable with null to continue processing other entries
   */
  private handlePrintingError(entry: Entry, error: unknown): Observable<null> {
    this.logger.error(
      new Error(
        `[PaymentsService] [printConsumptionEntries] [Error] Error printing consumption for entry ${entry.associated_id}`
      ),
      error
    );

    this.trackPayment('Error while printing consumption entry', {
      error: error instanceof Error ? error.message : String(error),
      customData: {entryId: entry.associated_id},
    });

    return of(null);
  }

  /**
   * Handles errors in the main print flow pipeline.
   *
   * @param error - The error object from the main print flow
   * @returns Observable with null to continue the payment process
   */
  private handleMainPrintFlowError(error: unknown): Observable<null> {
    this.trackPayment('Main print flow error, continuing the process...', {
      error: error instanceof Error ? error.message : String(error),
    });

    this.logger.error(
      new Error(
        `[PaymentsService] [printConsumptionEntries] Error in main print flow`
      ),
      error
    );

    return of(null);
  }
}
